import './scene-tree.scss';

import { Container, TreeView, TreeViewItem } from '@playcanvas/pcui';
import { Vec3 } from 'playcanvas';

import { duplicateMesh, duplicateSplat } from '../duplicate';
import { DuplicateElementOp, MeshRenameOp, SplitSplatOp, SplatRenameOp } from '../edit-ops';
import { Element, ElementType } from '../element';
import { Events } from '../events';
import { HelperPoint } from '../helper-point';
import { reparentPreserveWorld } from '../hierarchy';
import type { Mesh } from '../mesh';
import { applyOrbitToSceneCamera } from '../orbit-generator';
import type { OrbitParams } from '../orbit-generator';
import { createSceneCamera, setSceneCameraLookThrough } from '../scene-camera';
import type { Scene } from '../scene';
import { ShadowLight } from '../shadow-light';
import type { Splat } from '../splat';
import { Transform } from '../transform';
import { MenuItem, MenuPanel } from './menu-panel';

const getScene = (): Scene | null => {
    return ((window as any).scene as Scene) ?? null;
};

const isRootHelperName = (name: string) => {
    return typeof name === 'string' && name.endsWith('_root');
};

const getVehiclePrefixFromName = (name: string) => {
    const idx = name.indexOf('_');
    return idx > 0 ? name.slice(0, idx) : name;
};

const getHelperDisplayName = (helper: HelperPoint) => {
    // Keep internal helper.name stable (used in tests/contract), but show friendlier wheel roles when available.
    const name = String((helper as any).name ?? 'helper');
    const groupId = (helper as any).groupId;
    if (groupId && /_wheel[1-4]$/.test(name)) {
        const prefix = getVehiclePrefixFromName(name);
        return `${prefix}_wheel${groupId}`;
    }
    return name;
};

const isSceneTreeElement = (element: Element) => {
    // Skip internal elements and things managed by separate UI rows (e.g. Global Clip Box row).
    return element.type === ElementType.splat ||
           element.type === ElementType.model ||
           element.type === ElementType.helper ||
           element.type === ElementType.shadow;
};

const elementSortKey = (element: Element) => {
    // Prefer helpers first (vehicle hierarchies), then shadow/light helpers, then meshes, then splats.
    const typeOrder = element.type === ElementType.helper ? 0 : element.type === ElementType.shadow ? 1 : element.type === ElementType.model ? 2 : 3;
    const name = String((element as any).name ?? '');
    return { typeOrder, name: name.toLowerCase(), uid: element.uid };
};

class SceneTree extends Container {
    private events: Events;

    private treeView: TreeView;

    // TreeView root. PCUI TreeView currently prevents reparenting when a dragged item's parent is
    // the TreeView itself; by using a single explicit root item we enable dragging of all scene items
    // (including those shown at the "root" of the scene).
    private treeRootItem: TreeViewItem;

    // bidirectional lookup
    private elementToItem = new Map<Element, TreeViewItem>();
    private itemToElement = new Map<TreeViewItem, Element>();

    // context menu (guardrail: use context menu for actions until row buttons are stable)
    private contextMenu: MenuPanel;
    private outsideClickHandler: (event: PointerEvent) => void;

    // selection sync guard
    private syncingSelection = false;

    // If a user explicitly collapses a branch, don't auto-open it again when new children appear.
    private userCollapsedElements = new Set<Element>();

    // Drag lifecycle guard: avoid mutating TreeView DOM while PCUI is still inside its drag handlers.
    // If we rebuild/move items during `onReparent`, TreeView can miss cleanup and end up stuck.
    private treeDragging = false;
    private pendingHierarchyRefresh: { child?: Element; oldParent?: Element | null; newParent?: Element | null } | null = null;

    private getUniqueHelperName(scene: Scene, base: string) {
        const helpers = scene.getElementsByType(ElementType.helper) as any[];
        const names = new Set(helpers.map(h => String(h?.name ?? '')));
        if (!names.has(base)) return base;
        for (let i = 2; i < 10000; i++) {
            const n = `${base}${i}`;
            if (!names.has(n)) return n;
        }
        return `${base}_${Date.now()}`;
    }

    private findVehicleMotionRoot(element: Element | null): HelperPoint | null {
        let cur: any = element;
        for (let i = 0; i < 64 && cur; i++) {
            if (cur?.type === ElementType.helper && (cur as any).vehicleMotionController) return cur as HelperPoint;
            cur = cur?.parentElement;
        }
        return null;
    }

    private findCameraMotionRoot(element: Element | null): HelperPoint | null {
        let cur: any = element;
        for (let i = 0; i < 64 && cur; i++) {
            if (cur?.type === ElementType.helper && (cur as any).cameraMotionController) return cur as HelperPoint;
            cur = cur?.parentElement;
        }
        return null;
    }

    private getVehicleMotionSummary(root: HelperPoint) {
        const controller = (root as any).vehicleMotionController as any;
        if (!controller || typeof controller.getTracks !== 'function') {
            return null;
        }

        const tracks = controller.getTracks();
        const bodyKeys = tracks?.body?.frames?.length ?? 0;
        const wheelKeys = (tracks?.wheels ?? []).map((t: any) => t?.frames?.length ?? 0);
        const lastFrame = (() => {
            const frames = tracks?.body?.frames;
            if (!Array.isArray(frames) || frames.length === 0) return 0;
            return frames[frames.length - 1];
        })();
        return {
            controller,
            tracks,
            text: `Body ${bodyKeys} keys, Wheels ${wheelKeys.join('/')}, last @ frame ${Math.round(lastFrame)}`
        };
    }

    private async createEmptyHelper(parent: HelperPoint | null) {
        const scene = getScene();
        if (!scene) {
            this.showStatus('Cannot add helper: scene not ready');
            return;
        }

        const name = this.getUniqueHelperName(scene, 'null');
        const helper = new HelperPoint({
            name,
            animated: false,
            visible: true,
            parentEntity: parent ? parent.entity : null
        });

        // Establish logical hierarchy before add, so UI indentation is correct on first paint.
        if (parent) {
            helper.parentElement = parent;
            parent.childElements.push(helper as any);
        }

        await scene.add(helper as any);
        this.events.fire('hierarchy.changed', { child: helper as any, oldParent: null, newParent: parent as any });
        this.events.fire('selection', helper as any);
        this.showStatus(`Helper added: ${name}`);
    }

    private updateHelperItemClasses(element: Element, item: TreeViewItem) {
        if (!item || !element || element.type !== ElementType.helper) return;

        const helper = element as any as HelperPoint;
        const animated = !!(helper as any).animated;
        const hasMotion = !!(helper as any).vehicleMotionController || !!(helper as any).cameraMotionController;

        item.class[animated ? 'add' : 'remove']('scene-tree-item-helper-animated');
        item.class[hasMotion ? 'add' : 'remove']('scene-tree-item-helper-motion');
    }

    constructor(events: Events, args: any = {}) {
        args = {
            ...args,
            class: ['scene-tree', args.class].filter(Boolean)
        };

        super(args);

        this.events = events;

        this.contextMenu = new MenuPanel([]);
        // Mount on document.body so it is not clipped by overflow on panel containers.
        document.body.appendChild(this.contextMenu.dom);
        this.contextMenu.dom.classList.add('scene-tree-context-menu');
        this.contextMenu.dom.style.position = 'fixed';
        this.contextMenu.dom.style.zIndex = '10000';

        this.outsideClickHandler = (event: PointerEvent) => {
            const target = event.target as Node;
            if (!this.dom.contains(target) && !this.isInsideMenuTree(target, this.contextMenu)) {
                this.contextMenu.hidden = true;
            }
        };

        window.addEventListener('pointerdown', this.outsideClickHandler, true);
        window.addEventListener('pointerup', this.outsideClickHandler, true);

        this.treeView = new TreeView({
            allowDrag: true,
            allowReordering: false,
            allowRenaming: true,
            onReparent: (reparented: { item: TreeViewItem; oldParent: any; newParent?: any; newChildIndex?: number }[]) => {
                this.onTreeReparent(reparented as any);
            },
            onContextMenu: (evt: MouseEvent, item: TreeViewItem) => {
                this.onItemContextMenu(evt, item);
            }
        });

        this.append(this.treeView);

        // Track drag lifecycle so we can defer hierarchy UI rebuilds until after drag cleanup.
        this.treeView.on((TreeView as any).EVENT_DRAGSTART ?? 'dragstart', () => {
            this.treeDragging = true;
            // reset any previous queue
            this.pendingHierarchyRefresh = null;
        });
        this.treeView.on((TreeView as any).EVENT_DRAGEND ?? 'dragend', () => {
            this.treeDragging = false;
            const pending = this.pendingHierarchyRefresh;
            this.pendingHierarchyRefresh = null;
            if (!pending) return;

            // Apply the last known hierarchy change after drag has fully ended.
            if (pending.child) {
                this.rebuildSubtree(pending.child, pending.oldParent ?? null, pending.newParent ?? null);
            } else {
                this.rebuildAll();
            }
        });

        // Root item for the whole scene. Dropping an item inside this root unparents it (parent = null).
        this.treeRootItem = new TreeViewItem({
            text: 'Scene',
            icon: 'E360',
            open: true,
            allowDrag: false,
            allowDrop: true
        });
        this.treeRootItem.class.add('scene-tree-root');
        this.treeView.append(this.treeRootItem);

        // TreeView -> scene selection
        this.treeView.on(TreeView.EVENT_SELECT, (item: TreeViewItem) => {
            if (this.syncingSelection) return;
            const element = this.itemToElement.get(item);
            if (element) {
                this.events.fire('selection', element as any);
            }
        });

        this.treeView.on(TreeView.EVENT_DESELECT, (item: TreeViewItem) => {
            if (this.syncingSelection) return;
            const sel = this.events.invoke('selection') as any;
            const element = this.itemToElement.get(item);
            // Only clear selection if the user deselected the currently-selected element.
            if (element && sel === element) {
                this.events.fire('selection', null);
            }
        });

        this.treeView.on(TreeView.EVENT_RENAME, (item: TreeViewItem, newName: string) => {
            const element = this.itemToElement.get(item);
            if (!element) return;
            this.renameElement(element, item, newName);
        });

        // Scene -> tree sync (keep handler refs so we can unbind on destroy)
        const onElementAdded = (element: Element) => {
            this.addElementToTree(element);
        };

        const onElementRemoved = (element: Element) => {
            this.removeElementFromTree(element);
        };

        const onHierarchyChanged = (payload?: { child?: Element; oldParent?: Element | null; newParent?: Element | null }) => {
            const child = payload?.child;
            const oldParent = payload?.oldParent;
            const newParent = payload?.newParent;

            // If this arrived during a drag operation, defer the UI mutation until dragend.
            if (this.treeDragging || (this.treeView as any)?.isDragging) {
                // Keep only the latest change; it's enough to restore correctness.
                this.pendingHierarchyRefresh = payload ?? {};
                return;
            }

            if (child) {
                this.rebuildSubtree(child, oldParent ?? null, newParent ?? null);
            } else {
                // Some call sites fire this event with no args as a "something changed" signal.
                this.rebuildAll();
            }
        };

        const onSplatName = (splat: Splat) => {
            this.updateElementText(splat as any);
        };

        const onMeshName = (mesh: Mesh) => {
            this.updateElementText(mesh as any);
        };

        const onHelperName = (helper: HelperPoint) => {
            this.updateElementText(helper as any);
        };

        const onMeshLoaded = (mesh: Mesh) => {
            const item = this.elementToItem.get(mesh as any);
            if (item) {
                item.hidden = false;
                this.updateElementVisibilityClass(mesh as any);
            }
        };

        const onSplatVisibility = (splat: Splat) => {
            this.updateElementVisibilityClass(splat as any);
        };

        const onMeshVisibility = (mesh: Mesh) => {
            this.updateElementVisibilityClass(mesh as any);
        };

        const onHelperVisibility = (helper: HelperPoint) => {
            this.updateElementVisibilityClass(helper as any);
        };

        const onSelectionChanged = (selection: Element | null) => {
            this.syncSelection(selection);
        };

        events.on('scene.elementAdded', onElementAdded);
        events.on('scene.elementRemoved', onElementRemoved);
        events.on('hierarchy.changed', onHierarchyChanged);
        events.on('splat.name', onSplatName);
        events.on('mesh.name', onMeshName);
        events.on('helper.name', onHelperName);
        events.on('mesh.loaded', onMeshLoaded);
        events.on('splat.visibility', onSplatVisibility);
        events.on('mesh.visibility', onMeshVisibility);
        events.on('helper.visibility', onHelperVisibility);
        events.on('selection.changed', onSelectionChanged);

        // Best-effort initial build (UI is created before Scene is constructed; subsequent elementAdded events
        // will populate this automatically).
        this.rebuildAll();

        const superDestroy = this.destroy?.bind(this) || (() => {});
        this.destroy = () => {
            window.removeEventListener('pointerdown', this.outsideClickHandler, true);
            window.removeEventListener('pointerup', this.outsideClickHandler, true);

            // Unbind editor events (SceneTree can be recreated during hot reload / panel rebuild).
            events.off('scene.elementAdded', onElementAdded);
            events.off('scene.elementRemoved', onElementRemoved);
            events.off('hierarchy.changed', onHierarchyChanged);
            events.off('splat.name', onSplatName);
            events.off('mesh.name', onMeshName);
            events.off('helper.name', onHelperName);
            events.off('mesh.loaded', onMeshLoaded);
            events.off('splat.visibility', onSplatVisibility);
            events.off('mesh.visibility', onMeshVisibility);
            events.off('helper.visibility', onHelperVisibility);
            events.off('selection.changed', onSelectionChanged);

            this.contextMenu.dom.remove();
            superDestroy();
        };
    }

    private getDisplayName(element: Element): string {
        if (element.type === ElementType.helper) {
            return getHelperDisplayName(element as any);
        }

        const name = (element as any).name;
        if (typeof name === 'string' && name.length > 0) {
            return name;
        }

        return element.type;
    }

    private getIconForElement(element: Element): string {
        // PCUI expects an icon code like 'E360'.
        switch (element.type) {
            case ElementType.splat: return 'E421';
            case ElementType.model: return 'E425';
            case ElementType.helper: return 'E422';
            case ElementType.shadow: return 'E360';
            default: return 'E360';
        }
    }

    private attachItemToParent(element: Element, item: TreeViewItem) {
        // Detach from current parent (best-effort)
        try {
            (item.parent as any)?.remove?.(item);
        } catch (e) {
            // ignore
        }

        // Attach to new parent (as represented in data model).
        const parent = (element as any).parentElement as Element | null;
        const parentItem = parent ? this.elementToItem.get(parent) : undefined;

        if (parentItem) {
            parentItem.append(item);
        } else {
            this.treeRootItem.append(item);
        }
    }

    private addElementToTree(element: Element) {
        if (!element || !isSceneTreeElement(element)) return;

        const children = (element as any).childElements as Element[] | undefined;
        const isVehicleMotionRoot = element.type === ElementType.helper && !!(element as any).vehicleMotionController;
        const shouldAutoOpen = element.type === ElementType.helper && (isVehicleMotionRoot || ((children?.length ?? 0) > 0));
        const allowAutoOpen = shouldAutoOpen && !this.userCollapsedElements.has(element);

        // If the item already exists, ensure it's attached to the correct parent.
        // This is important when a child is added before its parent (event ordering).
        const existingItem = this.elementToItem.get(element);
        if (existingItem) {
            existingItem.text = this.getDisplayName(element);
            this.attachItemToParent(element, existingItem);
            this.updateElementVisibilityClass(element);
            this.updateHelperItemClasses(element, existingItem);

            if (allowAutoOpen) {
                existingItem.open = true;
            }

            // Ensure any eligible children exist as items and are attached under this parent.
            if (children && children.length) {
                for (const ch of children) {
                    if (ch && isSceneTreeElement(ch)) {
                        this.addElementToTree(ch);
                    }
                }
            }

            return;
        }

        const item = new TreeViewItem({
            text: this.getDisplayName(element),
            icon: this.getIconForElement(element),
            open: allowAutoOpen,
            allowDrag: true,
            allowDrop: true
        });

        // Track user intent around collapsing/expanding.
        item.on(TreeViewItem.EVENT_CLOSE, () => {
            const el = this.itemToElement.get(item);
            if (el) this.userCollapsedElements.add(el);
        });
        item.on(TreeViewItem.EVENT_OPEN, () => {
            const el = this.itemToElement.get(item);
            if (el) this.userCollapsedElements.delete(el);
        });

        // Styling hooks
        item.class.add('scene-tree-item');
        if (element.type === ElementType.splat) item.class.add('scene-tree-item-splat');
        if (element.type === ElementType.model) item.class.add('scene-tree-item-mesh');
        if (element.type === ElementType.helper) item.class.add('scene-tree-item-helper');
        this.updateHelperItemClasses(element, item);

        // Attach to parent item if present; otherwise add to root.
        this.attachItemToParent(element, item);

        this.elementToItem.set(element, item);
        this.itemToElement.set(item, element);

        this.updateElementVisibilityClass(element);

        // If the element already has eligible children, ensure they exist as items.
        if (children && children.length) {
            for (const ch of children) {
                if (ch && isSceneTreeElement(ch)) {
                    this.addElementToTree(ch);
                }
            }
        }
    }

    private removeElementFromTree(element: Element) {
        const item = this.elementToItem.get(element);
        if (!item) return;

        // Best-effort: if the element had children that remain in the scene, move them to root so the UI
        // does not lose them when we remove the parent item.
        const kids = (element as any).childElements as Element[] | undefined;
        if (kids && kids.length) {
            for (const k of kids) {
                const kidItem = this.elementToItem.get(k);
                if (kidItem) {
                    try {
                        (kidItem.parent as any)?.remove?.(kidItem);
                    } catch (e) {
                        // ignore
                    }
                    this.treeRootItem.append(kidItem);
                }
            }
        }

        try {
            (item.parent as any)?.remove?.(item);
        } catch (e) {
            // ignore
            try {
                this.treeView.remove(item);
            } catch (e2) {
                // ignore
            }
        }

        this.elementToItem.delete(element);
        this.itemToElement.delete(item);
    }

    private rebuildSubtree(child: Element, _oldParent: Element | null, _newParent: Element | null) {
        if (!child || !isSceneTreeElement(child)) return;

        // Ensure item exists.
        if (!this.elementToItem.has(child)) {
            this.addElementToTree(child);
        }

        const item = this.elementToItem.get(child);
        if (!item) return;

        // Detach from current parent.
        try {
            (item.parent as any)?.remove?.(item);
        } catch (e) {
            // ignore
        }

        // Attach to new parent (as represented in data model).
        const parent = (child as any).parentElement as Element | null;
        const parentItem = parent ? this.elementToItem.get(parent) : undefined;

        if (parentItem) {
            parentItem.append(item);
        } else {
            this.treeRootItem.append(item);
        }

        // Keep the moved subtree visible (in case it was moved under a collapsed parent).
        if (parentItem) {
            parentItem.open = true;
        }
    }

    private rebuildAll() {
        // Wipe UI
        this.treeView.clear();
        this.elementToItem.clear();
        this.itemToElement.clear();

        // Recreate root item (TreeView.clear removes all children).
        this.treeRootItem = new TreeViewItem({
            text: 'Scene',
            icon: 'E360',
            open: true,
            allowDrag: false,
            allowDrop: true
        });
        this.treeRootItem.class.add('scene-tree-root');
        this.treeView.append(this.treeRootItem);

        const scene = getScene();
        if (!scene) return;

        const allEligible: Element[] = [
            ...scene.getElementsByType(ElementType.helper),
            ...scene.getElementsByType(ElementType.shadow),
            ...scene.getElementsByType(ElementType.model),
            ...scene.getElementsByType(ElementType.splat)
        ];

        // Add roots first to preserve nesting.
        const eligibleSet = new Set(allEligible);

        const roots = allEligible
        .filter(e => !e.parentElement || !eligibleSet.has(e.parentElement))
        .sort((a, b) => {
            const ak = elementSortKey(a);
            const bk = elementSortKey(b);
            if (ak.typeOrder !== bk.typeOrder) return ak.typeOrder - bk.typeOrder;
            if (ak.name !== bk.name) return ak.name.localeCompare(bk.name);
            return ak.uid - bk.uid;
        });

        const visit = (el: Element) => {
            this.addElementToTree(el);
            const kids = (el as any).childElements as Element[] | undefined;
            if (!kids || kids.length === 0) return;

            // Keep a stable child order.
            const sorted = kids
            .filter(k => k && isSceneTreeElement(k))
            .slice()
            .sort((a, b) => {
                const ak = elementSortKey(a);
                const bk = elementSortKey(b);
                if (ak.typeOrder !== bk.typeOrder) return ak.typeOrder - bk.typeOrder;
                if (ak.name !== bk.name) return ak.name.localeCompare(bk.name);
                return ak.uid - bk.uid;
            });

            for (const k of sorted) {
                if (!this.elementToItem.has(k)) {
                    visit(k);
                }
            }
        };

        for (const r of roots) {
            if (!this.elementToItem.has(r)) {
                visit(r);
            }
        }

        // Catch stragglers (cycles / missing roots / out-of-band parent refs).
        for (const el of allEligible) {
            if (!this.elementToItem.has(el)) {
                visit(el);
            }
        }

        // Reflect current selection.
        const sel = this.events.invoke('selection') as Element | null;
        this.syncSelection(sel);
    }

    private showStatus(text: string) {
        // Prefer the global events bus (StatusBar listens on it). Also try the scene bus as a fallback.
        try {
            this.events.fire('status.text', text, { timeoutMs: 2500 });
        } catch (e) {
            // ignore
        }

        const scene = getScene();
        try {
            (scene as any)?.events?.fire?.('status.text', text, { timeoutMs: 2500 });
        } catch (e) {
            // ignore
        }
    }

    private isInsideMenuTree(target: Node, menu: MenuPanel): boolean {
        if (menu.dom.contains(target)) return true;
        for (const item of menu.menuItems) {
            if (item.subMenu && this.isInsideMenuTree(target, item.subMenu)) return true;
        }
        return false;
    }

    private isVideoTextureFile(file: File) {
        return /\.(?:webm|mp4)$/i.test(file.name);
    }

    private async pickImageFile(): Promise<File | null> {
        // Prefer the modern file picker when available.
        if (window.showOpenFilePicker) {
            try {
                const [handle] = await window.showOpenFilePicker({
                    multiple: false,
                    types: [
                        {
                            description: 'Image or Video',
                            accept: {
                                'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
                                'video/webm': ['.webm'],
                                'video/mp4': ['.mp4']
                            }
                        }
                    ]
                });
                return await handle.getFile();
            } catch (e) {
                // User cancelled or picker failed.
                return null;
            }
        }

        // Fallback to a hidden <input type="file">.
        return await new Promise<File | null>((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*,video/webm,video/mp4';
            input.style.display = 'none';

            let done = false;
            let onChange: (() => void) | null = null;
            let onFocus: (() => void) | null = null;

            const cleanup = () => {
                if (onChange) input.removeEventListener('change', onChange);
                if (onFocus) window.removeEventListener('focus', onFocus);
                input.remove();
            };

            const finish = (file: File | null) => {
                if (done) return;
                done = true;
                cleanup();
                resolve(file);
            };

            onChange = () => {
                const file = input.files && input.files[0] ? input.files[0] : null;
                finish(file);
            };

            // If the user cancels the dialog, "change" won't fire. When focus returns, resolve null.
            onFocus = () => {
                window.setTimeout(() => {
                    const file = input.files && input.files[0] ? input.files[0] : null;
                    finish(file);
                }, 0);
            };

            input.addEventListener('change', onChange);
            window.addEventListener('focus', onFocus);
            document.body.appendChild(input);
            input.click();
        });
    }

    private revertTreeItemPosition(item: TreeViewItem, oldParent: any) {
        if (!item || !oldParent) return;
        try {
            // Detach from current parent first.
            (item.parent as any)?.remove?.(item);
        } catch (e) {
            // ignore
        }

        try {
            // oldParent is a PCUI Element (TreeView / TreeViewItem). Append back.
            oldParent.append?.(item);
        } catch (e) {
            // ignore
        }
    }

    private onTreeReparent(reparented: { item: TreeViewItem; oldParent: any; newParent?: any; newChildIndex?: number }[]) {
        try {
            const scene = getScene();
            if (!scene) {
                this.showStatus('Cannot parent: scene not ready');
                // UI was not moved by TreeView when onReparent is supplied.
                return;
            }

            for (const r of reparented) {
                try {
                    const item = r.item;
                    const element = this.itemToElement.get(item);
                    if (!element) continue;

                    // When TreeView is constructed with onReparent, PCUI does not mutate the DOM. Use the computed
                    // newParent (if provided) rather than item.parent.
                    const newParentItem = (r as any).newParent ?? (item.parent as any);
                    const newParentElement = (!newParentItem || newParentItem === this.treeRootItem || newParentItem === this.treeView) ?
                        null :
                        (this.itemToElement.get(newParentItem) ?? null);

                    // Validate: reparentPreserveWorld has cycle detection built in.
                    const success = reparentPreserveWorld(element, newParentElement, scene);
                    if (!success) {
                        // Revert any TreeView UI move (best-effort). With onReparent, TreeView does not move DOM,
                        // but keep this for resilience if PCUI behavior changes.
                        this.revertTreeItemPosition(item, r.oldParent);
                        this.showStatus('Cannot parent: see console for details');
                    }
                } catch (e) {
                    console.warn('SceneTree: onTreeReparent item failed', e);
                }
            }
        } catch (e) {
            // Never let exceptions bubble into PCUI drag end handler; it can leave TreeView stuck dragging.
            console.warn('SceneTree: onTreeReparent failed', e);
        } finally {
            // Safety: ensure TreeView drag state is not left "on".
            try {
                const tv: any = this.treeView as any;
                if (tv && tv.isDragging) {
                    tv.isDragging = false;
                }
            } catch {
                // ignore
            }
        }
    }

    private updateElementText(element: Element) {
        const item = this.elementToItem.get(element);
        if (!item) return;

        item.text = this.getDisplayName(element);
    }

    private isElementVisible(element: Element): boolean {
        if (element.type === ElementType.model) {
            // MeshList uses renderVisible to hide meshes without disabling the entity.
            return !!(element as any as Mesh).renderVisible;
        }

        // splat/helper use visible
        const v = (element as any).visible;
        return v === undefined ? true : !!v;
    }

    private updateElementVisibilityClass(element: Element) {
        const item = this.elementToItem.get(element);
        if (!item) return;

        const visible = this.isElementVisible(element);
        if (visible) {
            item.class.remove('scene-tree-item-hidden');
        } else {
            item.class.add('scene-tree-item-hidden');
        }
    }

    private syncSelection(selection: Element | null) {
        this.syncingSelection = true;
        try {
            // Root is not mapped to a scene element; ensure it doesn't get stuck selected.
            if (this.treeRootItem) {
                this.treeRootItem.selected = false;
            }

            // Deselect all
            for (const item of this.itemToElement.keys()) {
                item.selected = false;
            }

            if (!selection) {
                return;
            }

            const item = this.elementToItem.get(selection);
            if (item) {
                item.selected = true;
                // Expand hierarchy to show the selected node.
                item.parentsOpen = true;
                item.focus();
            }
        } finally {
            this.syncingSelection = false;
        }
    }

    private renameElement(element: Element, item: TreeViewItem, newName: string) {
        const trimmed = typeof newName === 'string' ? newName.trim() : '';
        if (!trimmed) {
            // Revert UI to current name if the user cleared it.
            item.text = this.getDisplayName(element);
            return;
        }

        if (element.type === ElementType.splat) {
            this.events.fire('edit.add', new SplatRenameOp(element as any, trimmed));
            return;
        }

        if (element.type === ElementType.model) {
            this.events.fire('edit.add', new MeshRenameOp(element as any, trimmed));
            return;
        }

        if (element.type === ElementType.helper) {
            // Helpers do not currently have an undoable RenameOp.
            // Keep best-effort consistency: rename the helper entity too.
            (element as any as HelperPoint).name = trimmed;
            try {
                (element as any as HelperPoint).entity.name = trimmed;
            } catch (e) {
                // ignore
            }
            this.events.fire('helper.name', element as any);
            this.updateElementText(element);
        }
    }

    private onItemContextMenu(evt: MouseEvent, item: TreeViewItem) {
        evt.preventDefault();
        evt.stopPropagation();

        const resolvedItem = item ?? (() => {
            const target = evt.target as HTMLElement | null;
            const itemDom = target?.closest?.('.pcui-treeview-item') as unknown as { ui?: TreeViewItem } | null;
            return itemDom?.ui ?? null;
        })();

        const element = resolvedItem ? this.itemToElement.get(resolvedItem) : undefined;
        if (!element) {
            // Root / non-element items: allow scene-level actions.
            const scene = getScene();
            if (!scene) return;

            const existing = scene.getElementsByType(ElementType.shadow).find((e: any) => e instanceof ShadowLight) as ShadowLight | undefined;
            const menuItems: MenuItem[] = [
                {
                    text: 'Add Empty (Null)',
                    isEnabled: () => true,
                    onSelect: async () => {
                        await this.createEmptyHelper(null);
                    }
                },
                {
                    text: 'Add Camera',
                    isEnabled: () => true,
                    onSelect: async () => {
                        const cam = await createSceneCamera(scene, this.events);
                        this.events.fire('selection', cam as any);
                        this.showStatus(`Camera added: ${cam.name}`);
                    }
                },
                {},
                {
                    text: existing ? 'Select Shadow Light' : 'Add Shadow Light',
                    isEnabled: () => true,
                    onSelect: async () => {
                        if (existing) {
                            this.events.fire('selection', existing as any);
                            return;
                        }
                        const light = new ShadowLight();
                        await scene.add(light);
                        this.showStatus('Shadow Light added');
                        this.events.fire('selection', light as any);
                    }
                },
                {
                    text: 'Remove Shadow Light',
                    isEnabled: () => !!existing,
                    onSelect: () => {
                        if (!existing) return;
                        try {
                            existing.destroy();
                            this.showStatus('Shadow Light removed');
                        } catch (e) {
                            this.showStatus('Failed to remove Shadow Light');
                        }
                    }
                }
            ];

            this.contextMenu.setItems(menuItems);
            this.contextMenu.hidden = false;

            const style = this.contextMenu.dom.style;
            style.left = `${evt.clientX}px`;
            style.top = `${evt.clientY}px`;

            requestAnimationFrame(() => {
                if (this.contextMenu.hidden) return;
                const rect = this.contextMenu.dom.getBoundingClientRect();
                const pad = 6;
                const x = Math.min(evt.clientX, window.innerWidth - rect.width - pad);
                const y = Math.min(evt.clientY, window.innerHeight - rect.height - pad);
                style.left = `${Math.max(pad, x)}px`;
                style.top = `${Math.max(pad, y)}px`;
            });

            return;
        }

        // Select the item on right-click (matches common hierarchy UX).
        this.events.fire('selection', element as any);

        const menuItems: MenuItem[] = [];

        // All types
        menuItems.push({
            text: 'Rename',
            onSelect: () => {
                resolvedItem?.rename();
            }
        });

        menuItems.push({
            text: 'Set as Pivot',
            onSelect: () => {
                this.setPivotToElement(element);
            }
        });

        // Keep existing C.2 behavior: visibility toggle is provided via context menu.
        menuItems.push({
            text: this.isElementVisible(element) ? 'Hide' : 'Show',
            onSelect: () => {
                this.toggleVisibility(element);
            }
        });

        menuItems.push({});

        // Type-specific additions
        if (element.type === ElementType.splat) {
            const splat = element as any as Splat;
            menuItems.push({
                text: 'Duplicate',
                isEnabled: () => true,
                onSelect: () => {
                    const scene = getScene();
                    if (!scene) {
                        this.showStatus('Cannot duplicate: scene not ready');
                        return;
                    }

                    // Undoable true duplicate.
                    this.events.fire('edit.add', new DuplicateElementOp(scene, async () => {
                        return await duplicateSplat(splat, scene, 'true');
                    }));
                }
            });
            menuItems.push({
                text: 'Instance',
                // C.5: splat instances are only allowed if per-instance mutable state is independent.
                // Current repo stores selection/deletion state in GSplatData, which would leak.
                isEnabled: () => false,
                onSelect: () => this.showStatus('Splat Instance is disabled: selection/deletion state would leak across instances')
            });
            menuItems.push({
                text: 'Split Selected',
                // Enabled only when some (but not all) non-deleted gaussians are selected.
                isEnabled: () => {
                    // splat.numSelected/numSplats are updated by splat.updateState().
                    return (splat.numSelected > 0) && (splat.numSplats > 0) && (splat.numSelected < splat.numSplats);
                },
                onSelect: () => {
                    const scene = getScene();
                    if (!scene) {
                        this.showStatus('Cannot split: scene not ready');
                        return;
                    }

                    this.events.fire('edit.add', new SplitSplatOp(scene, splat));
                }
            });
            menuItems.push({});
        }

        if (element.type === ElementType.model) {
            const mesh = element as any as Mesh;
            const speedMenu = new MenuPanel([
                {
                    text: mesh.videoPlaybackRate === 0.5 ? '✓ 0.5x' : '0.5x',
                    onSelect: () => {
                        mesh.videoPlaybackRate = 0.5;
                        this.showStatus('Video speed set to 0.5x');
                    }
                },
                {
                    text: mesh.videoPlaybackRate === 1 ? '✓ 1x' : '1x',
                    onSelect: () => {
                        mesh.videoPlaybackRate = 1;
                        this.showStatus('Video speed set to 1x');
                    }
                },
                {
                    text: mesh.videoPlaybackRate === 2 ? '✓ 2x' : '2x',
                    onSelect: () => {
                        mesh.videoPlaybackRate = 2;
                        this.showStatus('Video speed set to 2x');
                    }
                }
            ]);
            const playbackModeMenu = new MenuPanel([
                {
                    text: mesh.videoPlaybackMode === 'realtime' ? '✓ Realtime' : 'Realtime',
                    onSelect: () => {
                        mesh.videoPlaybackMode = 'realtime';
                        this.showStatus('Video playback mode set to realtime');
                    }
                },
                {
                    text: mesh.videoPlaybackMode === 'per-frame' ? '✓ Per-Frame' : 'Per-Frame',
                    onSelect: () => {
                        mesh.videoPlaybackMode = 'per-frame';
                        this.showStatus('Video playback mode set to per-frame');
                    }
                }
            ]);
            menuItems.push({
                text: 'Duplicate',
                isEnabled: () => true,
                onSelect: () => {
                    const scene = getScene();
                    if (!scene) {
                        this.showStatus('Cannot duplicate: scene not ready');
                        return;
                    }

                    this.events.fire('edit.add', new DuplicateElementOp(scene, async () => {
                        return await duplicateMesh(mesh, scene, 'true');
                    }));
                }
            });
            menuItems.push({
                text: 'Instance',
                isEnabled: () => true,
                onSelect: () => {
                    const scene = getScene();
                    if (!scene) {
                        this.showStatus('Cannot instance: scene not ready');
                        return;
                    }

                    this.events.fire('edit.add', new DuplicateElementOp(scene, async () => {
                        return await duplicateMesh(mesh, scene, 'instance');
                    }));
                }
            });
            menuItems.push({
                text: mesh.hasTexture ? 'Replace Texture' : 'Import Texture',
                isEnabled: () => true,
                onSelect: async () => {
                    const file = await this.pickImageFile();
                    if (!file) return;
                    try {
                        if (this.isVideoTextureFile(file)) {
                            await mesh.loadVideoTexture(file);
                        } else {
                            await mesh.loadTexture(file);
                        }
                        this.showStatus(`Texture applied: ${file.name}`);
                    } catch (e) {
                        console.warn('Failed to load texture for mesh', mesh?.name, e);
                        this.showStatus('Failed to import texture');
                    }
                }
            });
            menuItems.push({
                text: 'Remove Texture',
                isEnabled: () => !!mesh.hasTexture,
                isVisible: () => !!mesh.hasTexture,
                onSelect: () => {
                    try {
                        mesh.removeTexture();
                        this.showStatus('Texture removed');
                    } catch (e) {
                        console.warn('Failed to remove texture for mesh', mesh?.name, e);
                        this.showStatus('Failed to remove texture');
                    }
                }
            });
            menuItems.push({
                text: mesh.useAlpha ? '✓ Use Texture Alpha' : 'Use Texture Alpha',
                isEnabled: () => !!mesh.hasTexture,
                isVisible: () => !!mesh.hasTexture,
                onSelect: () => {
                    mesh.useAlpha = !mesh.useAlpha;
                    this.showStatus(mesh.useAlpha ? 'Texture alpha enabled' : 'Texture alpha disabled');
                }
            });
            if (mesh.videoSource) {
                menuItems.push({
                    text: 'Playback Mode',
                    isEnabled: () => !!mesh.videoSource,
                    subMenu: playbackModeMenu
                });
                menuItems.push({
                    text: mesh.videoPlaying ? 'Pause Video' : 'Play Video',
                    isEnabled: () => !!mesh.videoSource && mesh.videoPlaybackMode === 'realtime',
                    onSelect: () => {
                        if (mesh.videoPlaying) {
                            mesh.pauseVideo();
                            this.showStatus('Video paused');
                        } else {
                            mesh.playVideo();
                            this.showStatus('Video playing');
                        }
                    }
                });
                menuItems.push({
                    text: mesh.videoLoop ? '✓ Loop Video' : 'Loop Video',
                    isEnabled: () => !!mesh.videoSource,
                    onSelect: () => {
                        mesh.videoLoop = !mesh.videoLoop;
                        this.showStatus(mesh.videoLoop ? 'Video loop enabled' : 'Video loop disabled');
                    }
                });
                menuItems.push({
                    text: 'Video Speed',
                    isEnabled: () => !!mesh.videoSource && mesh.videoPlaybackMode === 'realtime',
                    subMenu: speedMenu
                });
            }
            menuItems.push({
                text: mesh.billboard ? '✓ Billboard Mode' : 'Billboard Mode',
                isEnabled: () => true,
                onSelect: () => {
                    mesh.billboard = !mesh.billboard;
                    this.showStatus(mesh.billboard ? 'Billboard enabled' : 'Billboard disabled');
                }
            });
            menuItems.push({
                text: 'Split Sub-Meshes',
                // C.6: Best-effort; currently BLOCKED pending robust glTF sub-mesh extraction.
                isEnabled: () => false,
                onSelect: () => this.showStatus('Split Sub-Meshes is currently blocked: not implemented yet')
            });
            menuItems.push({
                text: 'Toggle Collision',
                onSelect: () => {
                    // Toggle collider mode (none <-> block). Matches legacy MeshList behavior.
                    mesh.collisionMode = (mesh.collisionMode === 'block') ? 'none' : 'block';
                    this.events.fire('mesh.collisionModeChanged', mesh);
                    this.showStatus(mesh.collisionMode === 'block' ? 'Collision enabled' : 'Collision disabled');
                }
            });
            menuItems.push({
                text: 'Toggle Shadow Catcher',
                isEnabled: () => true,
                onSelect: () => {
                    (mesh as any).shadowCatcher = !(mesh as any).shadowCatcher;
                    this.showStatus((mesh as any).shadowCatcher ? 'Shadow catcher enabled' : 'Shadow catcher disabled');
                }
            });
            menuItems.push({});
        }

        if (element.type === ElementType.helper) {
            const helper = element as any as HelperPoint;

            menuItems.push({
                text: 'Add Empty Child',
                isEnabled: () => !helper.animated,
                onSelect: async () => {
                    await this.createEmptyHelper(helper);
                }
            });

            menuItems.push({
                text: 'Add Camera Child',
                isEnabled: () => !helper.animated,
                onSelect: async () => {
                    const scene = getScene();
                    if (!scene) {
                        this.showStatus('Cannot add camera: scene not ready');
                        return;
                    }
                    const cam = await createSceneCamera(scene, this.events, undefined, helper);
                    this.events.fire('selection', cam as any);
                    this.showStatus(`Camera added: ${cam.name}`);
                }
            });

            // Scene camera look-through toggle
            const sceneCamData = (helper as any).sceneCameraData;
            if (sceneCamData) {
                menuItems.push({
                    text: sceneCamData.lookThrough ? 'Stop Look Through' : 'Look Through Camera',
                    isEnabled: () => true,
                    onSelect: () => {
                        try {
                            setSceneCameraLookThrough(helper, this.events, !sceneCamData.lookThrough);
                            this.showStatus(sceneCamData.lookThrough ? 'Looking through camera' : 'Stopped looking through camera');
                        } catch (e) {
                            this.showStatus('Failed to toggle Look Through Camera');
                        }
                    }
                });

                menuItems.push({
                    text: 'Generate Orbit…',
                    isEnabled: () => true,
                    onSelect: async () => {
                        // Use the camera's current world position as default orbit center target,
                        // and derive a reasonable default radius from the scene bounds.
                        const scene = getScene();
                        const camPos = helper.entity.getPosition();
                        const boundSize = scene ? scene.bound.halfExtents.length() : 5;
                        const defaultRadius = Math.max(1, boundSize * 0.3);

                        // Center = current camera target (forward from camera)
                        const rot = helper.entity.getRotation();
                        const fwd = new Vec3();
                        rot.transformVector(Vec3.FORWARD, fwd);
                        const defaultCenter = new Vec3().add2(camPos, fwd.mulScalar(defaultRadius));

                        const result = await this.events.invoke('show.orbitDialog', {
                            center: defaultCenter,
                            radius: defaultRadius
                        } as Partial<OrbitParams>);

                        if (!result) return;

                        const ok = applyOrbitToSceneCamera(helper, this.events, result as OrbitParams);
                        if (ok) {
                            this.showStatus(`Orbit generated: ${(result as OrbitParams).revolutions} rev, ${Math.round((result as OrbitParams).radius * 10) / 10}m radius`);
                        } else {
                            this.showStatus('Failed to generate orbit');
                        }
                    }
                });

                menuItems.push({});
            }

            const motionRoot = this.findVehicleMotionRoot(helper);
            if (motionRoot) {
                const summary = this.getVehicleMotionSummary(motionRoot);
                if (summary) {
                    menuItems.push({
                        text: 'Show Motion Keyframes…',
                        isEnabled: () => true,
                        onSelect: async () => {
                            await this.events.invoke('showPopup', {
                                type: 'info',
                                header: 'Vehicle Motion',
                                message: [
                                    'Motion is stored as keyframe tracks on the imported vehicle root helper (not as TRS changes on the root).',
                                    'Tip: parent your mesh/splat to *_body to follow motion; use *_root to offset/alignment the whole import.',
                                    '',
                                    `Root: ${motionRoot.name}`,
                                    summary.text
                                ].join('\n')
                            });
                        }
                    });

                    menuItems.push({
                        text: 'Copy Motion Tracks JSON',
                        isEnabled: () => !!(navigator as any)?.clipboard,
                        onSelect: async () => {
                            try {
                                await navigator.clipboard.writeText(JSON.stringify(summary.tracks));
                                this.showStatus('Copied motion tracks JSON');
                            } catch (e) {
                                this.showStatus('Failed to copy motion tracks');
                            }
                        }
                    });

                    menuItems.push({});
                }
            }

            const cameraRoot = this.findCameraMotionRoot(helper);
            if (cameraRoot && cameraRoot === helper) {
                const controller = (cameraRoot as any).cameraMotionController as any;
                if (controller && typeof controller.setLookThrough === 'function') {
                    menuItems.push({
                        text: controller.lookThrough ? 'Stop Look Through Camera' : 'Look Through Camera',
                        isEnabled: () => true,
                        onSelect: () => {
                            try {
                                controller.setLookThrough(!controller.lookThrough);
                                this.showStatus(controller.lookThrough ? 'Look Through Camera enabled' : 'Look Through Camera disabled');
                            } catch (e) {
                                this.showStatus('Failed to toggle Look Through Camera');
                            }
                        }
                    });

                    menuItems.push({});
                }
            }

            menuItems.push({
                text: 'Unparent Children',
                onSelect: () => {
                    this.unparentNonHelperChildren(element as any);
                }
            });
            menuItems.push({});
        }

        menuItems.push({
            text: 'Delete',
            isEnabled: () => this.canDeleteElement(element),
            onSelect: () => {
                this.deleteElementWithConfirm(element).catch((e) => {
                    console.warn('deleteElementWithConfirm failed', e);
                });
            }
        });

        this.contextMenu.setItems(menuItems);
        this.contextMenu.hidden = false;

        // Position at cursor. MenuPanel supports relative positioning, but for context menu we want fixed coords.
        const style = this.contextMenu.dom.style;
        style.left = `${evt.clientX}px`;
        style.top = `${evt.clientY}px`;

        // Clamp after layout so it stays on-screen.
        requestAnimationFrame(() => {
            if (this.contextMenu.hidden) return;
            const rect = this.contextMenu.dom.getBoundingClientRect();
            const pad = 6;
            const x = Math.min(evt.clientX, window.innerWidth - rect.width - pad);
            const y = Math.min(evt.clientY, window.innerHeight - rect.height - pad);
            style.left = `${Math.max(pad, x)}px`;
            style.top = `${Math.max(pad, y)}px`;
        });
    }

    private setPivotToElement(element: Element) {
        const pivot = this.events.invoke('pivot') as any;
        const entity = (element as any).entity as any;
        if (!pivot?.place || !entity?.getPosition || !entity?.getRotation) {
            this.showStatus('Cannot set pivot: pivot tool not available');
            return;
        }

        const t = new Transform();
        try {
            // Pivot expects world space.
            const pos = entity.getPosition().clone();
            const rot = entity.getRotation().clone();
            const scale = entity.getLocalScale?.() ?? entity.getScale?.();
            t.set(pos, rot, scale);
            pivot.place(t);
            this.showStatus('Pivot set');
        } catch (e) {
            console.warn('setPivotToElement failed', e);
            this.showStatus('Cannot set pivot: see console');
        }
    }

    private unparentNonHelperChildren(helper: HelperPoint) {
        const scene = getScene();
        if (!scene) {
            this.showStatus('Cannot unparent: scene not ready');
            return;
        }

        const kids = (helper.childElements ?? []).slice();
        let count = 0;
        for (const ch of kids) {
            if (!ch || ch.type === ElementType.helper) continue;
            if (reparentPreserveWorld(ch as any, null, scene)) {
                count++;
            }
        }
        this.showStatus(count > 0 ? `Unparented ${count} ${count === 1 ? 'child' : 'children'}` : 'No children to unparent');
    }

    private toggleVisibility(element: Element) {
        if (element.type === ElementType.splat) {
            const splat = element as any as Splat;
            splat.visible = !splat.visible;
            this.updateElementVisibilityClass(element);
            return;
        }

        if (element.type === ElementType.model) {
            const mesh = element as any as Mesh;
            // Toggle render-only visibility (matches MeshList behavior).
            mesh.renderVisible = !mesh.renderVisible;
            mesh.scene.forceRender = true;
            this.updateElementVisibilityClass(element);
            return;
        }

        if (element.type === ElementType.helper) {
            const helper = element as any as HelperPoint;
            helper.visible = !helper.visible;
            this.updateElementVisibilityClass(element);
        }
    }

    private canDeleteElement(element: Element): boolean {
        if (element.type === ElementType.splat) return true;
        if (element.type === ElementType.model) return true;

        if (element.type === ElementType.helper) {
            const helper = element as any as HelperPoint;
            // Allow deleting user-created empties and imported roots. Block deleting animated helpers.
            return !helper.animated;
        }

        return false;
    }

    private async deleteElementWithConfirm(element: Element) {
        if (!this.canDeleteElement(element)) {
            return;
        }

        if (element.type === ElementType.splat) {
            const splat = element as any as Splat;
            const result = await this.events.invoke('showPopup', {
                type: 'yesno',
                header: 'Remove Splat',
                message: `Are you sure you want to remove '${splat.name}' from the scene? This operation can not be undone.`
            });
            if (result?.action === 'yes') {
                splat.destroy();
            }
            return;
        }

        if (element.type === ElementType.model) {
            const mesh: any = element as any;
            const result = await this.events.invoke('showPopup', {
                type: 'yesno',
                header: 'Remove Mesh',
                message: `Are you sure you want to remove '${mesh.name}' from the scene? This operation can not be undone.`
            });
            if (result?.action === 'yes') {
                mesh.destroy();
            }
            return;
        }

        if (element.type === ElementType.helper) {
            const helper = element as any as HelperPoint;

            // Imported vehicle motion root helpers: delete as a bundle (root + animated children).
            if (isRootHelperName(String(helper.name ?? '')) && !helper.animated) {
                await this.deleteHelperBundle(helper);
                return;
            }

            // User-created helper: delete just this node (and preserve children).
            await this.deleteSingleHelperWithConfirm(helper);
        }
    }

    private async deleteSingleHelperWithConfirm(helper: HelperPoint) {
        const scene = getScene();
        if (!scene) return;

        if (helper.animated) return;

        const result = await this.events.invoke('showPopup', {
            type: 'yesno',
            header: 'Remove Helper',
            message: `Remove '${helper.name}' from the scene? Any children will be reparented up one level.`
        });

        if (result?.action !== 'yes') return;

        const newParent = (helper.parentElement as any) ?? null;
        const kids = (helper.childElements ?? []).slice();
        for (const ch of kids) {
            try {
                reparentPreserveWorld(ch as any, newParent, scene);
            } catch (e) {
                // best effort
            }
        }

        try {
            helper.destroy();
            this.showStatus('Helper removed');
        } catch (e) {
            console.warn('Failed to remove helper', helper?.name, e);
            this.showStatus('Failed to remove helper');
        }
    }

    private async deleteHelperBundle(helper: HelperPoint) {
        const scene = getScene();
        if (!scene) return;

        // Only allow deleting root helpers (and only if not animated).
        if (!isRootHelperName(String(helper.name ?? '')) || helper.animated) return;

        const hasVehicleController = !!(helper as any).vehicleMotionController;
        const hasCameraController = !!(helper as any).cameraMotionController;
        const header = hasVehicleController ?
            'Remove Vehicle Helpers' :
            (hasCameraController ? 'Remove Camera Helpers' : 'Remove Helper Bundle');
        const message = hasVehicleController ?
            `Remove '${helper.name}' and its imported animated helpers? Any parented meshes/splats will be unparented.` :
            (hasCameraController ?
                `Remove '${helper.name}' and its imported camera helpers? Any parented meshes/splats will be unparented.` :
                `Remove '${helper.name}' and its helper children? Any parented meshes/splats will be unparented.`);

        const result = await this.events.invoke('showPopup', {
            type: 'yesno',
            header,
            message
        });

        if (result?.action !== 'yes') return;

        // Best-effort: stop motion controller first.
        try {
            (helper as any).vehicleMotionController?.destroy?.();
        } catch (e) {
            // ignore
        }

        try {
            (helper as any).cameraMotionController?.destroy?.();
        } catch (e) {
            // ignore
        }

        // Collect helper subtree: root + helper children.
        const helperSubtree: HelperPoint[] = [helper];
        for (const kid of (helper.childElements ?? [])) {
            if (kid.type === ElementType.helper) helperSubtree.push(kid as any);
        }

        // Unparent any non-helper children from each helper in the subtree.
        for (const h of helperSubtree) {
            const nonHelperChildren = (h.childElements ?? []).filter(e => e.type !== ElementType.helper);
            for (const ch of nonHelperChildren) {
                reparentPreserveWorld(ch, null, scene);
            }
        }

        // Remove helper elements (children first, then root).
        for (let i = helperSubtree.length - 1; i >= 0; i--) {
            try {
                helperSubtree[i].destroy();
            } catch (e) {
                // ignore
            }
        }
    }
}

export { SceneTree };
